import { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import Icon from 'Components/Atoms/Icon/Icon';

const MAX_NAME_LENGTH = 128;

export default function InputFileButton(props) {
    const {
        onUpload,
        onError,
        children,
        multiple,
        acceptedFileTypes,
        maxFileSize,
        errorMessages: errorMessagesProp,
        isBusy,
        disabled,
        variant,
        size,
        className,
        iconSize = 20,
    } = props;

    const inputRef = useRef(null);

    const { t } = useTranslation('common');

    const errorMessages = {
        fileTooLarge: errorMessagesProp?.fileTooLarge || t('file-too-large'),
        invalidFileType: errorMessagesProp?.invalidFileType || t('invalid-file-type'),
        fileNameTooLong: errorMessagesProp?.fileNameTooLong || t('file-name-too-long'),
    };

    const emitError = (message) => {
        onError?.(message);
    };

    const hasInvalidSizes = (files) => {
        if (!maxFileSize) return false;
        return Array.from(files).some((file) => file.size > maxFileSize);
    };

    const hasInvalidFormats = (files) => {
        if (!acceptedFileTypes) return false;
        const acceptedFormats = acceptedFileTypes.split(',').map((format) => format.trim());
        return Array.from(files).some((file) => !acceptedFormats.includes(file.type));
    };

    const hasInvalidNames = (files) => {
        return Array.from(files).some((file) => file.name.length > MAX_NAME_LENGTH);
    };

    const resetInput = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const { files } = e.target;

        if (!files || files.length === 0) {
            resetInput();
            return;
        }

        if (hasInvalidSizes(files)) {
            emitError(errorMessages.fileTooLarge);
            resetInput();
            return;
        }

        if (hasInvalidFormats(files)) {
            emitError(errorMessages.invalidFileType);
            resetInput();
            return;
        }

        if (hasInvalidNames(files)) {
            emitError(errorMessages.fileNameTooLong);
            resetInput();
            return;
        }

        if (multiple) {
            onUpload(files);
        } else {
            onUpload(files[0]);
        }

        resetInput();
    };

    return (
        <>
            <Button
                onClick={handleClick}
                isBusy={isBusy}
                disabled={disabled}
                variant={variant}
                size={size}
                className={className}
            >
                <Icon name="attachment" color="currentColor" size={iconSize} />
                {children}
            </Button>
            <input
                ref={inputRef}
                type="file"
                name="files[]"
                className="hidden"
                accept={acceptedFileTypes}
                multiple={multiple}
                onChange={handleFileChange}
            />
        </>
    );
}

InputFileButton.propTypes = {
    onUpload: PropTypes.func.isRequired,
    onError: PropTypes.func,
    children: PropTypes.node,
    multiple: PropTypes.bool,
    acceptedFileTypes: PropTypes.string,
    maxFileSize: PropTypes.number,
    errorMessages: PropTypes.shape({
        fileTooLarge: PropTypes.string,
        invalidFileType: PropTypes.string,
        fileNameTooLong: PropTypes.string,
    }),
    isBusy: PropTypes.bool,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'primary', 'ghost', 'tertiary', 'warning']),
    size: PropTypes.oneOf(['default', 'small', 'extraSmall']),
    className: PropTypes.string,
    iconSize: PropTypes.number,
};
